import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Box,
  Divider,
  TextField,
  Alert
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { fetchWithAuth } from '../../utils/auth';
import { useInteractiveTutorial } from '../../utils/InteractiveTutorialContext';
import { getButtonStyles, BUTTON_COLORS } from '../../config/buttonStyles';
import { useTheme } from '../../utils/ThemeContext';
import { getThemeAwareColor } from '../../utils/categoryColors';

function CategorySelector({ 
  open, 
  onClose, 
  article, 
  categories,
  onCategoryAssigned,
  onCreateCategory 
}) {
  const { completeStep, isActive: isTutorialActive, getCurrentStep } = useInteractiveTutorial();
  const { isDarkMode } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && article) {
      // Set initial selection to article's current category
      setSelectedCategory(article.category_id || 'none');
      setError('');
      
      // Complete tutorial step when dialog opens (with small delay for UI to render)
      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'assign_category') {
        setTimeout(() => {
          completeStep('assign_category');
        }, 100);
      }
    }
  }, [open, article, isTutorialActive, getCurrentStep, completeStep]);

  const handleSave = async () => {
    if (!article) return;

    setLoading(true);
    setError('');

    try {
      const categoryId = selectedCategory === 'none' ? null : selectedCategory;
      
      const response = await fetchWithAuth(`/api/articles/categorize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query_id: article.query_id,
          article_index: article.article_index,
          category_id: categoryId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to categorize article');
      }

      // Notify parent component
      if (onCategoryAssigned) {
        onCategoryAssigned(article, categoryId);
      }
      
      // Complete tutorial step when saving category assignment
      if (isTutorialActive && getCurrentStep && getCurrentStep()?.id === 'save_category_assignment') {
        completeStep('save_category_assignment');
      }

      onClose();
    } catch (error) {
      console.error('Error categorizing article:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!article) return null;

  const currentCategory = categories.find(cat => cat.id === article.category_id);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--surface-2)',
          border: '1px solid var(--border)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--text-primary)' }}>
        Categorize Article
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Article Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'var(--surface-3)', borderRadius: '4px' }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: 'var(--text-primary)', 
              fontWeight: 600,
              mb: 0.5
            }}
          >
            {article.title || article.truncated_title}
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
            Query: {article.query_name || 'Unknown'}
          </Typography>
        </Box>

        {/* Current Category */}
        {currentCategory && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
              Current Category:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <span style={{ fontSize: '20px' }}>{currentCategory.icon}</span>
              <Typography sx={{ color: 'var(--text-primary)' }}>
                {currentCategory.name}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Category Selection */}
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'var(--text-primary)' }}>
          Select ONE category:
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {/* No Category Option */}
            <FormControlLabel
              value="none"
              control={<Radio />}
              label={
                <Typography sx={{ color: 'var(--text-muted)' }}>
                  No category (remove)
                </Typography>
              }
              sx={{
                mb: 1,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.9rem'
                }
              }}
            />

            <Divider sx={{ my: 1 }} />

            {/* User Categories */}
            {categories.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'var(--text-muted)', fontStyle: 'italic', p: 2 }}>
                No categories yet. Create one below!
              </Typography>
            ) : (
              categories.map((category) => {
                const themeColor = getThemeAwareColor(category.color, isDarkMode, 0);
                return (
                  <FormControlLabel
                    key={category.id}
                    value={category.id}
                    data-tutorial="category-option"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '18px' }}>{category.icon}</span>
                        <Typography sx={{ color: 'var(--text-primary)' }}>
                          {category.name}
                        </Typography>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: themeColor,
                            borderRadius: '50%',
                            ml: 'auto',
                            border: isDarkMode ? 'none' : `1px solid ${themeColor}`,
                            boxShadow: isDarkMode ? 'none' : `0 0 4px ${themeColor}40`
                          }}
                        />
                      </Box>
                    }
                    sx={{
                      mb: 0.5,
                      p: 1,
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'var(--surface-3)'
                      }
                    }}
                  />
                );
              })
            )}
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Create New Category Link */}
        <Button
          fullWidth
          size="small"
          startIcon={<AddIcon />}
          className="btn-modern"
          onClick={() => {
            onClose();
            if (onCreateCategory) {
              onCreateCategory();
            }
          }}
          sx={getButtonStyles(false, isDarkMode, 'generic')}
        >
          Create New Category
        </Button>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          className="btn-modern"
          sx={getButtonStyles(false, isDarkMode, 'generic')}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="btn-modern"
          disabled={loading || selectedCategory === ''}
          data-tutorial="category-assign-save"
          sx={getButtonStyles(false, isDarkMode, 'generic')}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CategorySelector;

